import { LocationApi, LocationSearchItem } from '@deckee/api-app-main';
import {
  Box,
  Card,
  Input,
  MessageBlock,
  NavList,
  SearchIcon,
} from '@deckee/deck-hand';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { validateLatitudeLongitude } from '@deckee/utilities';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { debounce } from 'throttle-debounce';
import { telemetry } from '../../../../../services/backend';
import apiConfiguration from '../../../../../services/backend/apiConfiguration';
import { captureEvent } from '../../../../../services/event-capture';
import { AppState, useAppDispatch } from '../../../../../services/store';
import {
  updatePoint,
  updatePointWithPlaceId,
} from '../../../../../services/store/modules/plan';
import BasicDrawerLayout from '../../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../../layouts/components/LayoutBody/LayoutBody';
import IconWithTextLockup from '../../../../lockups/IconWithTextLockup/IconWithTextLockup';
import BlueCircleWrapper from '../../../../shared/BlueCircleWrapper/BlueCircleWrapper';
import LogoSpinner from '../../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import { ReactSvg, map } from '../../../../shared/vector-images';
import LocationSearchCard from '../SearchLocationCard/LocationSearchCard';

const api = new LocationApi(apiConfiguration);

const DEFAULT_SEARCH_TERM = '';

const StyledLogoSpinner = styled(LogoSpinner)`
  width: 100%;
  height: 100% !important;
  min-height: 100px;
`;

const SearchLocationPage: FC = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setError] = useState(false);
  const dispatch = useAppDispatch();
  const { lat: mapLat, lon: mapLon } = useSelector(
    (state: AppState) =>
      state.appControls.currentMapParams.viewPortDetails.center,
  );

  const { pointId } = useParams<{ pointId: string }>();
  const {
    colors: { primary },
  } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLocationItems, setSearchLocationItems] = useState<
    LocationSearchItem[]
  >([]);

  const handleChooseOnMap = () => {
    captureEvent({
      type: 'plan: choose location from map',
      payload: {},
    });

    telemetry(
      telemetry.INFO,
      `[Plan.handleChooseOnMap] Selected point from choose on map ${pointId}`,
    );

    history.push(`/maps/plan/details/choose-location/${pointId}`);
  };

  const updateLocationSearchTerm = useMemo(
    () =>
      debounce(200, async (event, controller?: AbortController) => {
        try {
          if (controller?.signal?.aborted) {
            return;
          }

          if (!mapLat || !mapLon) {
            return;
          }
          setIsLoading(true);

          const termText = event.target.value;
          const searchTermValue = termText.trim();

          if (!searchTermValue) {
            setSearchLocationItems([]);
            setIsLoading(false);
            return;
          }

          const { data } = await api.locationsSearch(
            {
              locationsSearchRequest: {
                searchTerm: searchTermValue,
                latLon: {
                  lat: mapLat,
                  lon: mapLon,
                },
              },
            },
            {
              signal: controller?.signal,
            },
          );

          setSearchLocationItems(data);
        } catch (error) {
          if (controller?.signal?.aborted) {
            // It's not an issue, another request was initiated
            return;
          }
          setError(true);
          telemetry(
            telemetry.ERROR,
            `[Plan.updateLocationSearchTerm] Error retrieving locations ${JSON.stringify(
              error,
            )}`,
          );
        } finally {
          setIsLoading(false);
        }
      }),
    [mapLat, mapLon],
  );

  useEffect(() => {
    if (hasError) {
      setError(false);
    }

    const controller = new AbortController();
    updateLocationSearchTerm(
      { target: { value: searchTerm || DEFAULT_SEARCH_TERM } },
      controller,
    );

    return () => {
      controller.abort();
    };
  }, [searchTerm, updateLocationSearchTerm, hasError]);

  const handleLocationSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleSelectSearchLocationCard = async (
    searchItem: LocationSearchItem,
  ) => {
    setIsLoading(true);
    captureEvent({
      type: 'plan: search location',
      payload: {
        location: searchItem,
      },
    });

    telemetry(
      telemetry.INFO,
      `[Plan.handleSelectSearchLocationCard] Selected point from search ${JSON.stringify(
        searchItem,
      )}`,
    );

    // If there is a placeId, update the point with the placeId
    if (searchItem?.placeId) {
      try {
        await dispatch(
          updatePointWithPlaceId({
            placeId: searchItem.placeId,
            id: pointId,
            name: searchItem.title,
          }),
        ).unwrap();
      } catch (error) {
        setError(true);
        setIsLoading(false);

        telemetry(
          telemetry.ERROR,
          `[Plan.handleSelectSearchLocationCard] Error retrieving search places locations ${JSON.stringify(
            error,
          )}`,
        );
        return;
      }
    } else if (
      searchItem?.latitude &&
      searchItem?.longitude &&
      validateLatitudeLongitude(searchItem?.latitude, searchItem?.longitude)
    ) {
      dispatch(
        updatePoint({
          id: pointId,
          latitude: searchItem.latitude,
          longitude: searchItem.longitude,
          name: searchItem.title,
        }),
      );
    } else {
      setError(true);
      setIsLoading(false);

      telemetry(
        telemetry.ERROR,
        `[Plan.handleSelectSearchLocationCard] Error unable to obtained a valid point to update ${JSON.stringify(
          searchItem,
        )}`,
      );
      return;
    }

    setIsLoading(false);
    history.push(`/maps/plan/details/choose-location/${pointId}`);
  };

  return (
    <BasicDrawerLayout
      heading="Search location"
      onBack={history.goBack}
      onClose={null}
      showMenuBar={false}
    >
      <LayoutBody>
        {hasError && (
          <Box mb="2">
            <MessageBlock type="error">
              Unable search locations, please try again.
            </MessageBlock>
          </Box>
        )}
        <Box flex="1 1 0px" mb={2}>
          <Input
            autoFocus
            clearable
            type="text"
            aria-label="search"
            placeholder="Search for a location"
            startEnhancer={() => <SearchIcon color={primary} />}
            value={searchTerm}
            onChange={handleLocationSearchChange}
          />
        </Box>

        <Card>
          <NavList grouped>
            <NavList.Item chevron onClick={handleChooseOnMap}>
              <IconWithTextLockup
                heading="Choose on map"
                emphasis="heading"
                image={
                  <BlueCircleWrapper>
                    <ReactSvg
                      toDisplay={map()}
                      options={{
                        fillColor: '#001A70',
                      }}
                      width={24}
                      height={24}
                    />
                  </BlueCircleWrapper>
                }
              />
            </NavList.Item>
          </NavList>
        </Card>
        <Card position="relative" minHeight={isLoading ? '200px' : 'none'}>
          {isLoading && <StyledLogoSpinner background />}
          <NavList grouped>
            {!isLoading &&
              searchLocationItems &&
              searchLocationItems.length > 0 &&
              searchLocationItems.map((searchLocationItem) => (
                <LocationSearchCard
                  key={`${searchLocationItem.placeId}-${searchLocationItem.markerId}`}
                  title={searchLocationItem.title}
                  markerType={searchLocationItem.markerType}
                  placeDescription={searchLocationItem.placeDescription}
                  onSelectCard={() =>
                    handleSelectSearchLocationCard(searchLocationItem)
                  }
                />
              ))}
          </NavList>
        </Card>
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default SearchLocationPage;
